import { useEffect } from "react";
import { ShoppingBag, Upload, X } from "lucide-react";
import StepNumber from "./StepNumber";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

export default function PersonalisationModal({
  show,
  product,
  getPrimaryImage,
  requiresPhotoUpload,
  allowsCustomText,
  customTextLabel,
  customText,
  customTextMaxLength,
  setCustomText,
  uploadedFiles,
  uploadingFiles,
  handleUploadClick,
  removeUploadedFile,
  isPhotoPrintProduct,
  displayTotal,
  unitPrice,
  addPersonalisedToCartHandler,
  setShowPersonaliseModal,
}) {
  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowPersonaliseModal(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [show, setShowPersonaliseModal]);

  if (!show || !product) return null;

  const maxUploads = Number(product.max_uploads || 1);
  const hasPhotos = uploadedFiles.length > 0;
  const hasText = customText.trim().length > 0;

  const isPersonalisationIncomplete =
    (requiresPhotoUpload && !hasPhotos) || (allowsCustomText && !hasText);

  let step = 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1c1c1c]/70 px-4 py-6 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Personalise ${product.name}`}
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-[0_20px_80px_rgba(0,0,0,0.35)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-black/10 px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-pink-600">
              Personalise
            </p>
            <h2 className="mt-1 truncate text-xl font-semibold text-[#1c1c1c] sm:text-2xl">
              {product.name}
            </h2>
          </div>

          <button
            type="button"
            onClick={() => setShowPersonaliseModal(false)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/5 text-black/70 transition hover:bg-black/10 hover:text-black active:scale-95"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid flex-1 gap-4 overflow-y-auto bg-[#fff7fb] p-4 sm:p-6 md:grid-cols-[260px_1fr] md:gap-6">
          <div className="mx-auto w-28 md:sticky md:top-0 md:w-full md:self-start">
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-white">
              <img
                src={getPrimaryImage(product)}
                alt={product.name}
                className="aspect-square w-full object-contain"
              />
            </div>
          </div>

          <div className="space-y-4">
            {requiresPhotoUpload && (
              <section className="relative isolate overflow-hidden rounded-2xl border border-black/10 bg-white p-5">
                <StepNumber n={++step} />
                <div className="flex items-start gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-[#1c1c1c]">
                      Upload your photos{" "}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">(required)</span>
                    </h3>
                    <p className="mt-0.5 text-sm text-black/55">
                      Add up to {maxUploads} image{maxUploads > 1 ? "s" : ""}.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={`${file.name}_${index}`}
                      className="relative h-20 w-20 overflow-hidden rounded-xl border border-black/10 bg-black/5"
                    >
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeUploadedFile(index)}
                        aria-label="Remove image"
                        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </div>
                  ))}

                  {uploadedFiles.length < maxUploads && (
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      disabled={uploadingFiles}
                      className={[
                        "flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-dashed px-5 text-sm font-semibold transition",
                        uploadingFiles
                          ? "cursor-not-allowed border-pink-200 text-pink-300"
                          : "border-pink-300 text-pink-600 hover:border-pink-500 hover:bg-pink-50",
                      ].join(" ")}
                    >
                      <Upload size={18} aria-hidden="true" />
                      {uploadingFiles
                        ? "Uploading..."
                        : hasPhotos
                          ? "Add more"
                          : "Upload photos"}
                    </button>
                  )}
                </div>

                {hasPhotos && (
                  <p className="mt-3 text-xs text-black/55">
                    {uploadedFiles.length} of {maxUploads} uploaded
                  </p>
                )}
              </section>
            )}

            {allowsCustomText && (
              <section className="relative isolate overflow-hidden rounded-2xl border border-black/10 bg-white p-5">
                <StepNumber n={++step} />
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor="personalisation-text"
                      className="block text-base font-semibold text-[#1c1c1c]"
                    >
                      {customTextLabel}{" "}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">(required)</span>
                    </label>

                    <textarea
                      id="personalisation-text"
                      value={customText}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (
                          customTextMaxLength > 0 &&
                          value.length > customTextMaxLength
                        ) {
                          return;
                        }

                        setCustomText(value);
                      }}
                      rows={4}
                      placeholder={customTextLabel}
                      className="mt-3 w-full resize-none rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none transition focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10"
                    />

                    {customTextMaxLength > 0 && (
                      <p className="mt-2 text-right text-xs text-black/55">
                        {customText.trim().length} / {customTextMaxLength}{" "}
                        characters
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-black/10 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-pink-600">
              {isPhotoPrintProduct ? "Total" : "Price"}
            </p>
            <p className="text-xl font-bold text-black sm:text-2xl">
              {money(isPhotoPrintProduct ? displayTotal : unitPrice)}
            </p>
          </div>

          <div className="flex items-center gap-1 sm:gap-3">
            <button
              type="button"
              onClick={() => setShowPersonaliseModal(false)}
              className="rounded-full px-3 py-2.5 text-sm font-semibold text-black/60 transition hover:text-black sm:border sm:border-black/15 sm:px-6 sm:py-3 sm:text-[15px] sm:text-black/70 sm:hover:border-black/30"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={addPersonalisedToCartHandler}
              disabled={uploadingFiles || isPersonalisationIncomplete}
              className={[
                "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition sm:px-7 sm:py-3 sm:text-[15px]",
                uploadingFiles || isPersonalisationIncomplete
                  ? "cursor-not-allowed bg-black/15 text-black/40"
                  : "bg-pink-500 text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] hover:bg-pink-600",
              ].join(" ")}
            >
              <ShoppingBag size={18} aria-hidden="true" />
              {uploadingFiles
                ? "Uploading..."
                : isPersonalisationIncomplete
                  ? (
                    <>
                      <span className="sm:hidden">Complete fields</span>
                      <span className="hidden sm:inline">Complete required fields</span>
                    </>
                  )
                  : "Add to cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
